import { Box, Divider, Typography } from "@mui/material";
import { addDays, isSameDay } from "date-fns";
import React, { useCallback } from "react";

import useWorkerSchedulerData from "../controllers/use-worker-scheduler-data";
import FTWorkerViewCard from "./ft-worker-view-card";

interface WorkerShift {
  workerId: string;
  primaryJobName: string;
  workerName: string;
  firstName: string;
  lastName: string;
  shifts: ShiftCard[];
  dwCenterId: number;
  patternId: number;
  dPrimaryJobId: number;
  workerTypeId: string;
  workerSubTypeId: string;
  shiftId: string;
  shiftPatternName: string;
  shiftWorkcenter: string;
  shiftPrimaryJob: string;
  startDateTime: string;
  endDateTime: string;
  schedulingStatusId: number;
  previousSchedulingStatus?: string;
  isOverTimeShift?: boolean;
  isFlexShift?: boolean;
  isSameWorkcenter?: boolean;
  isSamePrimaryJob?: boolean;
  jobWorkcenters: {
    jobId: number;
    workCenterId: number;
    jobName: string;
    workCenterName: string;
  }[];
  primaryJobId?: number;
  workCenterId?: number;
  isJobChanged?: boolean;
  isWorkcenterChanged?: boolean;
  isPatternChanged?: boolean;
}

interface ShiftCard {
  uwCenterId: any;
  isFlexShift: boolean | undefined;
  shiftId: string;
  startDateTime: string;
  endDateTime: string;
  workcenterId: number;
  dwCenterId: number;
  workerId?: string;
  uPrimaryJobId: number;
  jobId: number;
  schedulingStatusId: number;
  previousSchedulingStatusId?: number;
  uPatternId: number;
  skillIds?: string;
}

interface WorkerShiftsDisplayProps {
  worker: WorkerShift;
  isPrinting?: boolean;
}

const MAIN_CONTENT_FLEX = 1;
const BORDER_COLOR = "#E5E5E5";

const WorkerShiftsDisplay = React.memo<WorkerShiftsDisplayProps>(
  ({ worker, isPrinting = false }) => {
    const { filterData, startingDay, isAttendanceManagementEnabled } =
      useWorkerSchedulerData();
    const {
      shiftPatternsObj,
      schedulingStatuses,
      allWorkcentersObj,
      allJobsObj,
    } = filterData;

    const renderDayShifts = useCallback(
      (day: Date, index: number) => {
        const allShifts = [...worker.shifts];

        const shifts = allShifts.filter((shift) =>
          isSameDay(new Date(shift.startDateTime), day)
        );

        return (
          <Box
            key={index}
            flex={1}
            width={isPrinting ? `${50 / 7}%` : "164px"}
            sx={{
              borderLeft: `1px solid ${BORDER_COLOR}`,
              overflowX: "hidden",
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              sx={{
                height: isPrinting ? "auto" : "100%",
                minHeight: isPrinting ? "unset" : "80px",
              }}
            >
              {shifts.length === 0 ? (
                // Empty day content
                <Box />
              ) : (
                // Day with shifts
                shifts.map((shift) => (
                  <FTWorkerViewCard
                    key={`${worker.workerId}-${shift.shiftId}`}
                    workerId={shift.workerId}
                    shiftId={shift.shiftId}
                    shiftWorkcenter={
                      shift.uwCenterId ||
                      shift.workcenterId ||
                      shift.dwCenterId ||
                      0
                    }
                    shiftPrimaryJob={shift.uPrimaryJobId || shift.jobId || 0}
                    startDateTime={shift.startDateTime}
                    endDateTime={shift.endDateTime}
                    isFlexShift={shift.isFlexShift}
                    isSameWorkcenter={shift.uwCenterId === worker.workCenterId}
                    isSamePrimaryJob={
                      shift.uPrimaryJobId === worker.primaryJobId
                    }
                    isSameShiftPattern={shift.uPatternId === worker.patternId}
                    schedulingStatus={
                      schedulingStatuses.find(
                        (status) => status.id === shift.schedulingStatusId
                      )?.code || "SCH"
                    }
                    previousSchedulingStatus={
                      schedulingStatuses.find(
                        (status) =>
                          status.id === shift.previousSchedulingStatusId
                      )?.code || "SCH"
                    }
                    workerFullName={`${worker.firstName} ${worker.lastName}`}
                    shiftPatternName={
                      !shift.isFlexShift ? worker.patternId !== shift.uPatternId
                        ? shiftPatternsObj?.[shift.uPatternId]?.name ?? ""
                        : shiftPatternsObj?.[worker.patternId]?.name ?? ""
                        : ""
                    }
                    workerJobWorkcenter={worker.jobWorkcenters}
                    isAttendanceManagementEnabled={
                      isAttendanceManagementEnabled
                    }
                    workerPrimaryJobName={
                      allJobsObj?.[worker.primaryJobId ?? 0]?.name
                    }
                    workerPrimaryWorkcenterName={
                      allWorkcentersObj?.[worker.workCenterId ?? 0]?.name
                    }
                    isPrinting={isPrinting}
                    skillIds={shift.skillIds}
                    isMoved={
                      worker.isJobChanged ||
                      worker.isWorkcenterChanged ||
                      worker.isPatternChanged
                    }
                  />
                ))
              )}
            </Box>
          </Box>
        );
      },
      [
        worker,
        shiftPatternsObj,
        isAttendanceManagementEnabled,
        schedulingStatuses,
        allWorkcentersObj,
        allJobsObj,
        isPrinting,
      ]
    );

    return (
      <Box
        sx={{
          display: "flex",
          flex: MAIN_CONTENT_FLEX,
          height: "100%",
        }}
      >
        {Array.from({ length: 7 }).map((_, index) => {
          const day = addDays(startingDay, index);
          return renderDayShifts(day, index);
        })}
      </Box>
    );
  }
);

WorkerShiftsDisplay.displayName = "WorkerShiftsDisplay";

export default WorkerShiftsDisplay;
